import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Plus, Loader2, Star, Share2, Trash2, Pencil, Rocket,
  ClipboardList, ChevronUp, ChevronDown, X, Send, MessageSquare, Upload, FileText,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

const SECTIONS = [
  { type: "etude_besoins", title: "Etude des besoins", desc: "Collectez les besoins et attentes des apprenants avant de construire le plan de formation." },
  { type: "positionnement", title: "Test de positionnement", desc: "Diagnostic initial pour evaluer le niveau des apprenants et adapter la formation." },
  { type: "evaluation_fin_formation", title: "Evaluation de fin de formation", desc: "Validez les acquis pedagogiques a l'issue de la formation (QCM, mises en situation, evaluation sommative)." },
  { type: "satisfaction_stagiaire", title: "Questionnaire de satisfaction stagiaire", desc: "Recueillez la satisfaction de l'apprenant a chaud, a froid ou sur un temps defini." },
  { type: "satisfaction_entreprise", title: "Questionnaire de satisfaction entreprise", desc: "Impliquez les managers et prescripteurs dans votre demarche qualite Qualiopi." },
  { type: "satisfaction_financeur", title: "Questionnaire de satisfaction financeur", desc: "OPCO, France Travail, CPF : tracez la satisfaction des organismes financeurs." },
  { type: "satisfaction_formateur", title: "Questionnaire de satisfaction formateur", desc: "Donnez la parole aux formateurs pour ameliorer le deroulement et le contenu." },
];

const QUESTION_TYPES = [
  { v: "rating", l: "Notation (etoiles)" },
  { v: "nps", l: "Net Promoter Score (0-10)" },
  { v: "radio", l: "Choix unique" },
  { v: "checkbox", l: "Choix multiples" },
  { v: "text", l: "Texte libre" },
  { v: "yesno", l: "Oui / Non" },
];

export default function EvaluationsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState({ pre: true, hot: true, cold: true, manager: true });
  const [editTpl, setEditTpl] = useState(null);
  const [newSection, setNewSection] = useState(null);
  const [importingFor, setImportingFor] = useState(null);
  const importInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/evaluations/templates");
      setTemplates(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (type) => setOpenSection(s => ({ ...s, [type]: !s[type] }));
  const fav = async (id) => {
    try { await api.post(`/evaluations/templates/${id}/favorite`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce modele ?")) return;
    await api.delete(`/evaluations/templates/${id}`);
    toast.success("Supprime"); load();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !importingFor) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("eval_type", importingFor);
    fd.append("name", file.name.replace(/\.[^.]+$/, ""));
    const tid = toast.loading("Analyse du document...");
    try {
      const r = await api.post("/evaluations/templates/import-from-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.dismiss(tid);
      const detected = r.data?.questions?.length || 0;
      toast.success(`Modele importe : ${detected} question(s) detectee(s)`);
      await load();
      // Open editor on the freshly created template
      setEditTpl(r.data);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || err.message || "Erreur d'import");
    }
    setImportingFor(null);
    if (importInputRef.current) importInputRef.current.value = "";
  };

  const triggerImport = (evalType) => {
    setImportingFor(evalType);
    setTimeout(() => importInputRef.current?.click(), 50);
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="evaluations-page">
      <PageHeader
        icon={<ClipboardList />}
        title="Bibliothèque d'évaluations"
        description="Collectez les retours des stagiaires et managers avant, pendant et après la formation."
        testId="evaluations-header"
      />
      <div className="mb-6">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#38bdf8]/5 border border-[#38bdf8]/15 text-xs text-slate-300">
          <FileText className="w-4 h-4 text-[#38bdf8] flex-shrink-0" />
          <span>Astuce : importez un questionnaire existant (DOCX / PDF) via le bouton <strong className="text-[#38bdf8]">Importer</strong> de chaque section. L'IA détecte automatiquement les questions.</span>
        </div>
      </div>

      {/* Hidden file input for OCR import */}
      <input
        ref={importInputRef}
        type="file"
        accept=".docx,.pdf,.html,.htm"
        onChange={handleImportFile}
        className="hidden"
        data-testid="eval-import-file-input"
      />

      {loading ? <ListSkeleton items={5} testId="evaluations-loading-skeleton" /> :
        <div className="space-y-4">
          {SECTIONS.map(sec => {
            const list = templates.filter(t => t.eval_type === sec.type);
            const isOpen = openSection[sec.type];
            return (
              <div key={sec.type} className="bg-[#14101c] border border-white/10 rounded-2xl overflow-hidden" data-testid={`eval-section-${sec.type}`}>
                <button onClick={() => toggle(sec.type)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 group">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-[#f7c935]" />
                    <div className="text-left">
                      <div className="font-bold">{sec.title}</div>
                      <div className="text-xs text-slate-400">{sec.desc}</div>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 space-y-2">
                    {list.length === 0 ? (
                      <div className="text-sm text-slate-500 py-4 text-center border border-dashed border-white/10 rounded">Aucun modele</div>
                    ) : (
                      list.map(t => (
                        <div key={t.template_id} className="bg-[#0b0811] border border-white/5 rounded p-3 flex items-center justify-between gap-2" data-testid={`tpl-${t.template_id}`}>
                          <button onClick={() => setEditTpl(t)} className="flex-1 text-left hover:text-[#f7c935] transition-colors">
                            <div className="font-semibold">{t.name}</div>
                            {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
                            <div className="text-[10px] text-slate-600 mt-1">{t.questions?.length || 0} questions · {t.auto_send ? "Envoi auto" : "Envoi manuel"}</div>
                          </button>
                          <Button size="sm" onClick={() => setEditTpl({ ...t, __sendMode: true })} className="bg-[#f7c935]/10 hover:bg-[#f7c935]/20 text-[#f7c935] text-xs" data-testid={`send-tpl-${t.template_id}`}>
                            <Rocket className="w-3 h-3 mr-1" /> Envoi et relance auto
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => fav(t.template_id)} className={t.is_favorite ? "text-yellow-400" : "text-slate-500"}><Star className={`w-4 h-4 ${t.is_favorite ? "fill-current" : ""}`} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(t.template_id); toast.success("ID copie"); }} className="text-slate-500"><Share2 className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => del(t.template_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      ))
                    )}
                    <button onClick={() => setNewSection(sec.type)} className="flex items-center gap-2 text-xs text-[#f7c935] hover:text-white px-2 py-1" data-testid={`add-tpl-${sec.type}`}>
                      <Plus className="w-4 h-4" /> Ajouter un modele
                    </button>
                    <button onClick={() => triggerImport(sec.type)} className="flex items-center gap-2 text-xs text-[#38bdf8] hover:text-white px-2 py-1 ml-3" data-testid={`import-tpl-${sec.type}`}>
                      <Upload className="w-4 h-4" /> Importer DOCX / PDF
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>}

      {(editTpl || newSection) && (
        <TemplateEditor
          template={editTpl}
          defaultType={newSection}
          sendMode={editTpl?.__sendMode}
          api={api}
          onClose={(saved) => { setEditTpl(null); setNewSection(null); if (saved) load(); }}
        />
      )}
    </div>
  );
}

// ─── Editor (create/edit template + send flow) ─────────────
function TemplateEditor({ template, defaultType, sendMode, api, onClose }) {
  const [form, setForm] = useState(
    template ? { ...template } : { name: "", eval_type: defaultType || "satisfaction_stagiaire", description: "", questions: [], auto_send: false, auto_reminder_days: 7 }
  );
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(sendMode ? "send" : "edit");

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      if (template && template.template_id) {
        await api.put(`/evaluations/templates/${template.template_id}`, form);
      } else {
        await api.post("/evaluations/templates", form);
      }
      toast.success("Enregistre");
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const addQuestion = () => {
    setForm(f => ({
      ...f,
      questions: [...(f.questions || []), { type: "rating", label: "", required: false, min_value: 1, max_value: 5, options: [] }],
    }));
  };

  const updQ = (idx, patch) => setForm(f => { const q = [...f.questions]; q[idx] = { ...q[idx], ...patch }; return { ...f, questions: q }; });
  const delQ = (idx) => setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-3xl bg-[#0b0811] border border-white/10 text-white" data-testid="tpl-editor">
        <DialogHeader>
          <DialogTitle>{template?.template_id ? "Modifier le modele" : "Nouveau modele d'evaluation"}</DialogTitle>
        </DialogHeader>

        {template?.template_id && (
          <div className="flex gap-1 border-b border-white/10 mb-3">
            <button onClick={() => setTab("edit")} className={`px-3 py-2 text-xs font-semibold ${tab === "edit" ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400"}`}>Structure</button>
            <button onClick={() => setTab("send")} className={`px-3 py-2 text-xs font-semibold ${tab === "send" ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400"}`} data-testid="eval-send-tab">Envoi &amp; relance</button>
            <button onClick={() => setTab("responses")} className={`px-3 py-2 text-xs font-semibold ${tab === "responses" ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400"}`}>Reponses</button>
          </div>
        )}

        {tab === "edit" && (
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
            <Input placeholder="Nom du modele" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="eval-name" autoFocus />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.eval_type} onChange={e => setForm({ ...form, eval_type: e.target.value })} className={`${inputCls} px-3 py-2 rounded`} data-testid="eval-type">
                <option value="etude_besoins">Etude des besoins</option>
                <option value="positionnement">Test de positionnement</option>
                <option value="evaluation_fin_formation">Evaluation de fin de formation</option>
                <option value="satisfaction_stagiaire">Satisfaction stagiaire</option>
                <option value="satisfaction_entreprise">Satisfaction entreprise</option>
                <option value="satisfaction_financeur">Satisfaction financeur</option>
                <option value="satisfaction_formateur">Satisfaction formateur</option>
              </select>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.auto_send} onChange={e => setForm({ ...form, auto_send: e.target.checked })} />Envoi auto</label>
            </div>
            <Textarea placeholder="Description" value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />

            <div className="pt-3 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm">Questions ({(form.questions || []).length})</h3>
                <Button size="sm" onClick={addQuestion} className="bg-[#f7c935] text-[#14101c] text-xs" data-testid="add-q-btn"><Plus className="w-3 h-3 mr-1" />Question</Button>
              </div>
              {(form.questions || []).map((q, i) => (
                <div key={i} className="bg-[#14101c] border border-white/10 rounded p-3 mb-2 space-y-2" data-testid={`q-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs bg-[#f7c935]/10 text-[#f7c935] font-bold px-2 py-0.5 rounded">Q{i + 1}</span>
                    <Input placeholder="Libelle de la question" value={q.label} onChange={e => updQ(i, { label: e.target.value })} className={inputCls} />
                    <Button size="sm" variant="ghost" onClick={() => delQ(i)} className="text-red-400"><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={q.type} onChange={e => updQ(i, { type: e.target.value })} className={`${inputCls} px-2 py-1 rounded text-xs`}>
                      {QUESTION_TYPES.map(qt => <option key={qt.v} value={qt.v}>{qt.l}</option>)}
                    </select>
                    {(q.type === "rating" || q.type === "nps") && (
                      <>
                        <Input type="number" value={q.min_value || 0} onChange={e => updQ(i, { min_value: Number(e.target.value) })} placeholder="Min" className={`${inputCls} text-xs`} />
                        <Input type="number" value={q.max_value || 5} onChange={e => updQ(i, { max_value: Number(e.target.value) })} placeholder="Max" className={`${inputCls} text-xs`} />
                      </>
                    )}
                    <label className="flex items-center gap-1 text-xs col-span-3"><input type="checkbox" checked={!!q.required} onChange={e => updQ(i, { required: e.target.checked })} />Obligatoire</label>
                  </div>
                  {(q.type === "radio" || q.type === "checkbox") && (
                    <div className="space-y-1">
                      {(q.options || []).map((o, oi) => (
                        <div key={oi} className="flex gap-1">
                          <Input value={o} onChange={e => { const opts = [...q.options]; opts[oi] = e.target.value; updQ(i, { options: opts }); }} className={`${inputCls} text-xs`} placeholder={`Option ${oi + 1}`} />
                          <Button size="sm" variant="ghost" onClick={() => updQ(i, { options: q.options.filter((_, x) => x !== oi) })} className="text-red-400"><X className="w-3 h-3" /></Button>
                        </div>
                      ))}
                      <Button size="sm" onClick={() => updQ(i, { options: [...(q.options || []), ""] })} className="bg-white/5 text-xs"><Plus className="w-3 h-3 mr-1" />Option</Button>
                    </div>
                  )}
                </div>
              ))}
              {(form.questions || []).length === 0 && <div className="text-center text-sm text-slate-500 py-4">Aucune question. Cliquez sur "Question" pour en ajouter.</div>}
            </div>
          </div>
        )}

        {tab === "send" && <SendPanel template={form} api={api} />}
        {tab === "responses" && <ResponsesPanel templateId={template?.template_id} api={api} />}

        <div className="flex justify-end gap-2 mt-3">
          <Button variant="ghost" onClick={() => onClose(false)}>Fermer</Button>
          {tab === "edit" && (
            <Button onClick={save} disabled={saving || !form.name.trim()} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="save-tpl-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Enregistrer
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Send panel: pick session + targets + send ─────────────
function SendPanel({ template, api }) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [enrollments, setEnrollments] = useState([]);
  const [selected, setSelected] = useState({});
  const [managerEmails, setManagerEmails] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/lms/sessions").then(r => setSessions(r.data || [])).catch(() => {});
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!sessionId) { setEnrollments([]); return; }
    api.get(`/lms/sessions/${sessionId}/enrollments`).then(r => setEnrollments(r.data || [])).catch(() => {});
  // eslint-disable-next-line
  }, [sessionId]);

  const send = async () => {
    if (!template.template_id) { toast.error("Enregistrez d'abord le modele"); return; }
    if (!sessionId) { toast.error("Selectionnez une session"); return; }
    setSending(true);
    try {
      const payload = { template_id: template.template_id, session_id: sessionId };
      if (template.eval_type === "satisfaction_entreprise" || template.eval_type === "manager") {
        payload.target_type = "manager";
        payload.manager_emails = managerEmails.split(",").map(s => s.trim()).filter(Boolean);
      } else {
        payload.target_type = "student";
        const ids = Object.keys(selected).filter(k => selected[k]);
        if (ids.length > 0) payload.enrollment_ids = ids;
      }
      const r = await api.post("/evaluations/send", payload);
      toast.success(`${r.data.sent_count} envoi(s) cree(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
      <div>
        <label className="text-xs text-slate-400 mb-1 block">Session cible</label>
        <select value={sessionId} onChange={e => { setSessionId(e.target.value); setSelected({}); }} className={`${inputCls} px-3 py-2 rounded w-full text-sm`} data-testid="send-session">
          <option value="">— Selectionner —</option>
          {sessions.map(s => <option key={s.session_id} value={s.session_id}>{s.title || s.program_title} ({s.start_date})</option>)}
        </select>
      </div>
      {template.eval_type === "satisfaction_entreprise" || template.eval_type === "manager" ? (
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Emails des managers (separes par virgule)</label>
          <Textarea value={managerEmails} onChange={e => setManagerEmails(e.target.value)} rows={3} placeholder="manager1@ent.com, manager2@ent.com" className={inputCls} />
        </div>
      ) : (
        <div>
          <label className="text-xs text-slate-400 mb-1 block">Stagiaires (vide = tous)</label>
          <div className="space-y-1 max-h-48 overflow-y-auto border border-white/10 rounded p-2">
            {enrollments.length === 0 ? <div className="text-xs text-slate-500">Aucun stagiaire inscrit</div> :
              enrollments.map(e => (
                <label key={e.enrollment_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/5 px-2 py-1 rounded">
                  <input type="checkbox" checked={!!selected[e.enrollment_id]} onChange={ev => setSelected(s => ({ ...s, [e.enrollment_id]: ev.target.checked }))} />
                  <span className="flex-1">{e.student_name}</span>
                  <span className="text-xs text-slate-500">{e.student_email}</span>
                </label>
              ))}
          </div>
        </div>
      )}
      <Button onClick={send} disabled={sending || !sessionId} className="bg-[#f7c935] text-[#14101c] font-bold w-full" data-testid="send-now-btn">
        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        Envoyer maintenant
      </Button>
    </div>
  );
}

function ResponsesPanel({ templateId, api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!templateId) return;
    api.get(`/evaluations/responses?template_id=${templateId}`).then(r => { setRows(r.data || []); setLoading(false); });
  // eslint-disable-next-line
  }, [templateId]);
  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#f7c935] mx-auto my-6" />;
  if (rows.length === 0) return <div className="text-sm text-slate-500 text-center py-6">Aucune reponse pour ce modele</div>;
  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
      {rows.map(r => (
        <div key={r.response_id} className="bg-[#14101c] border border-white/10 rounded p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{r.respondent_email}</span>
            <div className="flex gap-2 text-xs">
              {r.avg_rating !== null && <span className="bg-[#f7c935]/10 text-[#f7c935] px-2 py-0.5 rounded">★ {r.avg_rating}/5</span>}
              {r.nps !== null && <span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded">NPS {r.nps}</span>}
            </div>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">{r.submitted_at?.slice(0, 16).replace("T", " ")}</div>
        </div>
      ))}
    </div>
  );
}
